// store.ts — typed front-end wrappers over the on-device Rust SQLite + parsing + rollups.
// Every call hits LOCAL SQLite / local Rust via Tauri IPC (durable, offline, on-disk).
// NOTHING here talks to a server — the ONE AI call lives in brain.ts and receives only
// the small derived slice computed by `buildQuerySlice`.

import { invoke } from "./tauri-web";

// ── Domain types (mirror src-tauri/src/db.rs) ──────────────────────────────────
export interface Account {
  id: string;
  name: string;
  kind: string; // "bank" | "card" | "other"
  created_at: string;
}

export interface Txn {
  id: string;
  account_id: string;
  posted_on: string;      // YYYY-MM-DD
  merchant: string;
  raw_description: string;
  amount: number;         // signed MINOR units; debits negative, credits positive
  currency: string;
  category: string;
  imported_at: string;
  dedupe_key: string;
}

export interface ParsedTxn {
  posted_on: string;
  merchant: string;
  raw_description: string;
  amount: number;
  currency: string;
}

export interface ParsePreview {
  format: "csv" | "pdf";
  parsed: number;
  currency: string;
  sample: ParsedTxn[];
}

export interface ImportResult {
  inserted: number;
  duplicates: number;
  account_id: string;
  account_name: string;
}

// ── Rollup types (mirror src-tauri/src/rollups.rs) ─────────────────────────────
export interface CategoryTotal { category: string; spend: number; txn_count: number }
export interface MerchantTotal { merchant: string; spend: number; txn_count: number; category: string }
export interface MonthTotal { month: string; spend: number; income: number; net: number; txn_count: number }
export interface Recurring {
  merchant: string; category: string; typical_amount: number; occurrences: number;
  cadence: "monthly" | "weekly" | "irregular"; first_seen: string; last_seen: string;
}
export interface Overview {
  schema: string;
  generated_for: string;
  currency: string;
  total_txns: number;
  date_range: [string, string] | null;
  total_spend: number;
  total_income: number;
  by_category: CategoryTotal[];
  top_merchants: MerchantTotal[];
  by_month: MonthTotal[];
  recurring: Recurring[];
}
export interface MomDelta {
  category: string; current: number; prior: number; delta: number;
  pct_change: number | null;
  direction: "up" | "down" | "flat";
}
export type FactTone = "up" | "down" | "neutral";
export interface HeadlineFact {
  kind: "total" | "top_category" | "mover" | "merchant" | "recurring" | "income" | "coverage";
  text: string;
  tone: FactTone;
}
export interface QuerySlice {
  schema: string;
  question: string;
  currency: string;
  window: [string, string];
  window_label: string;
  window_txn_count: number;
  window_spend: number;
  window_income: number;
  window_net: number;
  prior_window_spend: number;
  prior_window_txn_count: number;
  spend_delta: number;
  spend_pct_change: number | null;
  spend_direction: "up" | "down" | "flat";
  avg_spend_per_day: number;
  by_category: CategoryTotal[];
  top_merchants: MerchantTotal[];
  by_month: MonthTotal[];
  mom_deltas: MomDelta[];
  recurring: Recurring[];
  recurring_in_window: number;
  recurring_monthly_total: number;
  /** Pre-ranked, ready-to-say observational sentences the ONE narration is grounded in. */
  headline_facts: HeadlineFact[];
}

// ── IPC surface ────────────────────────────────────────────────────────────────
export const store = {
  // accounts
  listAccounts: () => invoke<Account[]>("list_accounts"),
  renameAccount: (id: string, name: string) => invoke<void>("rename_account", { id, name }),
  deleteAccount: (id: string) => invoke<void>("delete_account", { id }),

  // transactions
  listTxns: (opts?: { from?: string; to?: string; accountId?: string; limit?: number }) =>
    invoke<Txn[]>("list_txns", { from: opts?.from, to: opts?.to, accountId: opts?.accountId, limit: opts?.limit ?? 2000 }),
  countTxns: () => invoke<number>("count_txns"),
  setCategory: (id: string, category: string) => invoke<void>("set_category", { id, category }),
  deleteTxn: (id: string) => invoke<void>("delete_txn", { id }),

  // import — NATIVE: Rust reads the dialog-selected `path` directly (no fs scope needed in the
  // WebView). WEB: there is no path — the sheet reads the picked file's text and passes `content`
  // (parse-web.ts does the parsing in the browser). Exactly one of path/content is set per surface.
  parseStatement: (args: { path?: string; content?: string; defaultCurrency?: string }) =>
    invoke<ParsePreview>("parse_statement", { path: args.path, content: args.content, defaultCurrency: args.defaultCurrency }),
  importStatement: (args: { path?: string; content?: string; accountName: string; accountKind?: string; defaultCurrency?: string }) =>
    invoke<ImportResult>("import_statement", {
      path: args.path, content: args.content, accountName: args.accountName, accountKind: args.accountKind, defaultCurrency: args.defaultCurrency,
    }),

  // rollups
  computeOverview: () => invoke<Overview>("compute_overview"),
  /** Pre-LLM advice fence. Returns refusal copy if the question seeks advice, else null. */
  adviceCheck: (question: string) => invoke<string | null>("advice_check", { question }),
  /** Build the scoped slice sent on the ONE AI call. `today` = client local YYYY-MM-DD. */
  buildQuerySlice: (args: { question: string; from?: string; to?: string; today: string }) =>
    invoke<QuerySlice>("build_query_slice", { question: args.question, from: args.from, to: args.to, today: args.today }),
};

// ── Categories (must mirror src-tauri/src/categorize.rs CATEGORIES) ─────────────
export const CATEGORIES = [
  "groceries", "dining", "transport", "fuel", "shopping", "utilities",
  "rent_mortgage", "subscriptions", "entertainment", "health", "travel",
  "cash", "fees", "income", "transfers", "uncategorized",
] as const;
export type Category = (typeof CATEGORIES)[number];

const CATEGORY_LABELS: Record<string, string> = {
  groceries: "Groceries", dining: "Dining & takeaway", transport: "Transport", fuel: "Fuel",
  shopping: "Shopping", utilities: "Utilities & bills", rent_mortgage: "Rent / mortgage",
  subscriptions: "Subscriptions", entertainment: "Entertainment", health: "Health & fitness",
  travel: "Travel", cash: "Cash", fees: "Fees & charges", income: "Income",
  transfers: "Transfers", uncategorized: "Uncategorized",
};
export function categoryLabel(c: string): string {
  return CATEGORY_LABELS[c] ?? (c ? c.charAt(0).toUpperCase() + c.slice(1) : "Uncategorized");
}

// ── Money formatting ────────────────────────────────────────────────────────────
const CURRENCY_SYMBOL: Record<string, string> = { GBP: "£", USD: "$", EUR: "€", AUD: "$", CAD: "$", NZD: "$", JPY: "¥", CHF: "CHF " };
export function currencySymbol(code: string): string {
  return CURRENCY_SYMBOL[code?.toUpperCase()] ?? (code ? code + " " : "");
}

/** Format a MAJOR-unit number (rollups already divide by 100) as currency. */
export function fmtMajor(value: number, currency: string): string {
  const sym = currencySymbol(currency);
  const abs = Math.abs(value);
  const body = abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${value < 0 ? "-" : ""}${sym}${body}`;
}

/** Format a signed MINOR-unit transaction amount (from db) as currency. */
export function fmtMinor(minor: number, currency: string): string {
  return fmtMajor(minor / 100, currency);
}

// ── Date helpers ─────────────────────────────────────────────────────────────
export const formatLocalDate = (d: Date): string =>
  `${d.getFullYear().toString().padStart(4, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
export const todayLocal = (): string => formatLocalDate(new Date());

export function fmtDate(s: string): string {
  if (!s) return "";
  return new Date(s.slice(0, 10) + "T00:00:00Z").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}
export function fmtMonth(ym: string): string {
  // "2026-03" -> "Mar 2026"
  if (!/^\d{4}-\d{2}$/.test(ym)) return ym;
  return new Date(ym + "-01T00:00:00Z").toLocaleDateString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
}

// ── Example questions shown in the Ask UI (all observational, none advice) ──────
export const EXAMPLE_QUESTIONS = [
  "How much did I spend on groceries last month?",
  "What were my top merchants this month?",
  "How did my dining spend change vs the previous month?",
  "Which subscriptions am I paying for?",
  "Summarise my spending for last month.",
];
